import classNames from 'classnames';
import type { Op } from 'quill/core';
import Quill, { Parchment } from 'quill/core';
import React from 'react';
import * as styles from './CommentInput.module.scss';
import CommentToolbar from './CommentToolbar';
import { MOD_KEY } from '../../constants/os';
import EmojiAutocompleter from '../../editor/controls/EmojiAutocompleter';
import MentionPicker from '../../editor/controls/MentionPicker';
import LinkCursorSelection from '../../editor/LinkCursorSelection';
import { isPrecedingCharAllowed } from '../../editor/PrefixListener';
import QuillComponent from '../../editor/QuillComponent';
import { useQuill, withQuillContextProvider } from '../../editor/QuillContext';
import type QuillEditor from '../../editor/QuillEditor';
import useLinkShortcut from '../../editor/useLinkShortcut';
import useEventListener from '../../utils/useEventListener';

const insertText = (quill: Quill, text: string) => {
  const { index, length } = quill.getSelection(true);
  quill.insertText(index + length, text, Quill.sources.USER);
  quill.setSelection(index + length + text.length, 0, Quill.sources.USER);
};

interface CommentInputConsumerViewProps {
  quill: QuillEditor | null;
  initialContent: Op[];
  autoFocus: boolean;
  onSave: () => void;
  onTextChange: (newContent: Op[]) => void;
  disabled: boolean;
}

const CommentInputConsumerView = ({
  quill,
  initialContent,
  autoFocus,
  onSave,
  onTextChange,
  disabled,
}: CommentInputConsumerViewProps) => {
  useLinkShortcut();

  useEventListener(
    quill?.root,
    'keydown',
    (event) => {
      if (event.key === 'Enter' && event[MOD_KEY]) {
        event.stopPropagation();
        event.preventDefault();
        onSave();
      }
    },
    {},
  );

  return (
    <div className={classNames('comment-editor', styles.container)}>
      {quill && (
        <>
          <CommentToolbar quill={quill} />
          <EmojiAutocompleter quill={quill} readOnly={false} />
        </>
      )}
      <QuillComponent
        noTitleLine
        namespace="comment"
        contentPlaceholder="Speak your mind"
        moduleConfig={{ prefixListener: true }}
        initialContent={initialContent}
        readOnly={disabled}
        onTextChange={() => {
          if (quill) {
            const actualContent = quill.getContents().ops;
            onTextChange(actualContent);
          }
        }}
        autoFocus={autoFocus}
      />
      {quill && (
        <>
          <LinkCursorSelection />
          <MentionPicker quill={quill} readOnly={false} />
        </>
      )}
    </div>
  );
};

interface CommentInputProps {
  autoFocus: boolean;
  initialContent: Op[];
  children?: (childProps: {
    input: JSX.Element;
    mentionUser: () => void;
    insertText: (text: string) => void;
  }) => React.ReactNode;
  onChange: (newContent: Op[]) => void;
  onSave: () => void;
  disabled?: boolean;
}

const CommentInput = ({
  autoFocus,
  initialContent = [],
  children,
  onChange,
  onSave,
  disabled,
}: CommentInputProps) => {
  const { quill } = useQuill();

  const input = (
    <CommentInputConsumerView
      quill={quill}
      initialContent={initialContent}
      autoFocus={autoFocus}
      onSave={onSave}
      onTextChange={onChange}
      disabled={!!disabled}
    />
  );

  return (
    <>
      {typeof children === 'function'
        ? children({
            input,
            mentionUser: () => {
              if (!quill) return;
              const { index, length } = quill.getSelection(true);
              const targetIndex = index + length;
              const [leaf, offset] = quill.getLeaf(targetIndex);

              if (leaf instanceof Parchment.TextBlot) {
                const char = leaf.value()[offset - 1];
                if (!isPrecedingCharAllowed(char)) {
                  insertText(quill, ' @');
                  return;
                }
              }
              insertText(quill, '@');
            },
            insertText: (text) => {
              if (quill) {
                insertText(quill, text);
              }
            },
          })
        : input}
    </>
  );
};

export default withQuillContextProvider(CommentInput);
